import * as THREE from 'three';
import type { TerrainSurfaceProbe } from './TerrainSurfaceProbe';

const OVERLAY_RADIUS_METERS = 15;
const GRID_STEP_METERS = 1.25;
const HEIGHT_OFFSET_METERS = 0.07;

export class SlopeReadOverlay {
  readonly object3D = new THREE.Group();
  private heatmapMesh: THREE.Mesh | null = null;
  private readonly heatmapMaterial = new THREE.MeshBasicMaterial({
    vertexColors: true,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  private opacity = 0;
  private targetVisible = false;
  private lastCenter = new THREE.Vector3(Number.POSITIVE_INFINITY, 0, Number.POSITIVE_INFINITY);

  constructor(private readonly probe: TerrainSurfaceProbe) {
    this.object3D.name = 'GreenReadHeatmapOverlay';
    this.object3D.visible = false;
  }

  setVisible(visible: boolean): void {
    this.targetVisible = visible;
  }

  update(center: THREE.Vector3, _elapsedSeconds: number, deltaSeconds: number): void {
    this.opacity = THREE.MathUtils.damp(this.opacity, this.targetVisible ? 1 : 0, 6, deltaSeconds);
    this.object3D.visible = this.opacity > 0.01;
    this.heatmapMaterial.opacity = this.opacity * 0.58;
    if (!this.object3D.visible) return;
    if (this.lastCenter.distanceToSquared(center) > 2.25) this.rebuild(center);
  }

  dispose(): void {
    this.clearOverlay();
    this.heatmapMaterial.dispose();
  }

  private rebuild(center: THREE.Vector3): void {
    this.clearOverlay();
    this.lastCenter.copy(center);
    this.heatmapMesh = new THREE.Mesh(this.buildHeatmapGeometry(center), this.heatmapMaterial);
    this.object3D.add(this.heatmapMesh);
  }

  private buildHeatmapGeometry(center: THREE.Vector3): THREE.BufferGeometry {
    const vertices: number[] = [];
    const colors: number[] = [];
    const indices: number[] = [];
    const indexByGrid = new Map<string, number>();
    const color = new THREE.Color();
    const cells = Math.floor((OVERLAY_RADIUS_METERS * 2) / GRID_STEP_METERS);

    for (let ix = 0; ix <= cells; ix += 1) {
      for (let iz = 0; iz <= cells; iz += 1) {
        const x = -OVERLAY_RADIUS_METERS + ix * GRID_STEP_METERS;
        const z = -OVERLAY_RADIUS_METERS + iz * GRID_STEP_METERS;
        if (x * x + z * z > OVERLAY_RADIUS_METERS * OVERLAY_RADIUS_METERS) continue;
        const wx = center.x + x;
        const wz = center.z + z;
        const sample = this.probe.sample(wx, wz);
        vertices.push(wx, sample.height + HEIGHT_OFFSET_METERS, wz);
        this.heatmapColor(sample.normal, color);
        colors.push(color.r, color.g, color.b);
        indexByGrid.set(`${ix},${iz}`, vertices.length / 3 - 1);
      }
    }

    for (let ix = 0; ix < cells; ix += 1) {
      for (let iz = 0; iz < cells; iz += 1) {
        const a = indexByGrid.get(`${ix},${iz}`);
        const b = indexByGrid.get(`${ix + 1},${iz}`);
        const c = indexByGrid.get(`${ix},${iz + 1}`);
        const d = indexByGrid.get(`${ix + 1},${iz + 1}`);
        if (a === undefined || b === undefined || c === undefined || d === undefined) continue;
        indices.push(a, b, c, b, d, c);
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    return geometry;
  }

  private heatmapColor(normal: THREE.Vector3, color: THREE.Color): void {
    const downhill = new THREE.Vector3(0, -1, 0).projectOnPlane(normal).setY(0);
    const severity = THREE.MathUtils.clamp((downhill.length() - 0.004) / 0.075, 0, 1);
    const green = new THREE.Color(0x1ed760);
    const yellow = new THREE.Color(0xffea3d);
    const orange = new THREE.Color(0xff5f2e);
    if (severity < 0.5) color.copy(green).lerp(yellow, severity * 2);
    else color.copy(yellow).lerp(orange, (severity - 0.5) * 2);
  }

  private clearOverlay(): void {
    if (this.heatmapMesh) {
      this.object3D.remove(this.heatmapMesh);
      this.heatmapMesh.geometry.dispose();
      this.heatmapMesh = null;
    }
  }
}
